// ========================================================================
// 🤧 SERVICIO DE GESTIÓN DE ALERGIAS
// ========================================================================

import {
  API_BASE_ALLERGY_URL,
  API_ALLERGY_LIST,
  API_ALLERGY_DETAILS,
  API_ALLERGY_CREATE,
  API_ALLERGY_UPDATE,
  API_ALLERGY_DELETE,
  API_ALLERGY_HARD_DELETE,
  API_ALLERGY_BY_PATIENT,
  API_ALLERGY_BULK_ACTION,
  API_ALLERGY_EXPORT,
  API_ALLERGY_STATISTICS
} from '../../../../helpers/urlHelper'; 


// Funciones helper para construir URLs completas
export const getAllergyListUrl = () => `${API_BASE_ALLERGY_URL}${API_ALLERGY_LIST}`;
export const getAllergyDetailsUrl = (id) => `${API_BASE_ALLERGY_URL}${API_ALLERGY_DETAILS}/${id}`;
export const getAllergyCreateUrl = () => `${API_BASE_ALLERGY_URL}${API_ALLERGY_CREATE}`;
export const getAllergyUpdateUrl = (id) => `${API_BASE_ALLERGY_URL}${API_ALLERGY_UPDATE}/${id}`;
export const getAllergyDeleteUrl = (id) => `${API_BASE_ALLERGY_URL}${API_ALLERGY_DELETE}/${id}`;
export const getAllergyHardDeleteUrl = (id) => `${API_BASE_ALLERGY_URL}${API_ALLERGY_HARD_DELETE}/${id}/hard`;
export const getAllergyByPatientUrl = (patientId) => `${API_BASE_ALLERGY_URL}${API_ALLERGY_BY_PATIENT}/${patientId}`;
export const getAllergyBulkActionUrl = () => `${API_BASE_ALLERGY_URL}${API_ALLERGY_BULK_ACTION}`;
export const getAllergyExportUrl = () => `${API_BASE_ALLERGY_URL}${API_ALLERGY_EXPORT}`;
export const getAllergyStatisticsUrl = () => `${API_BASE_ALLERGY_URL}${API_ALLERGY_STATISTICS}`;

// Ejemplos de uso:
// GET    - getAllergyListUrl() -> /api/allergies
// GET    - getAllergyDetailsUrl("64a1b2c3d4e5f6789abcdef0") -> /api/allergies/64a1b2c3d4e5f6789abcdef0
// GET    - getAllergyByPatientUrl("64a1b2c3d4e5f6789abcdef1") -> /api/allergies/patient/64a1b2c3d4e5f6789abcdef1
// POST   - getAllergyCreateUrl() -> /api/allergies
// PUT    - getAllergyUpdateUrl("64a1b2c3d4e5f6789abcdef0") -> /api/allergies/64a1b2c3d4e5f6789abcdef0
// DELETE - getAllergyDeleteUrl("64a1b2c3d4e5f6789abcdef0") -> /api/allergies/64a1b2c3d4e5f6789abcdef0
// DELETE - getAllergyHardDeleteUrl("64a1b2c3d4e5f6789abcdef0") -> /api/allergies/64a1b2c3d4e5f6789abcdef0/hard

// ========================================================================
// 📋 SERVICIOS CRUD BÁSICOS
// ========================================================================

export const getAllAllergies = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `${getAllergyListUrl()}?${queryParams}` : getAllergyListUrl();
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al obtener alergias:', error);
    throw error;
  }
};

export const getAllergyById = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de alergia es requerido');
    }

    const response = await fetch(getAllergyDetailsUrl(id), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al obtener alergia por ID:', error);
    throw error;
  }
};

export const getAllergiesByPatient = async (patientId) => {
  try {
    if (!patientId) {
      throw new Error('ID del paciente es requerido');
    }

    const response = await fetch(getAllergyByPatientUrl(patientId), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al obtener alergias del paciente:', error);
    throw error;
  }
};

export const createAllergy = async (allergyData) => {
  try {
    if (!allergyData) {
      throw new Error('Datos de la alergia son requeridos');
    }

    const response = await fetch(getAllergyCreateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(allergyData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al crear alergia:', error);
    throw error;
  }
};

export const updateAllergy = async (id, allergyData) => {
  try {
    if (!id) {
      throw new Error('ID de alergia es requerido');
    }
    if (!allergyData) {
      throw new Error('Datos de actualización son requeridos');
    }

    const response = await fetch(getAllergyUpdateUrl(id), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(allergyData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al actualizar alergia:', error);
    throw error;
  }
};

export const deleteAllergy = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de alergia es requerido');
    }

    const response = await fetch(getAllergyDeleteUrl(id), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al eliminar alergia:', error);
    throw error;
  }
};

export const hardDeleteAllergy = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de alergia es requerido');
    }

    const response = await fetch(getAllergyHardDeleteUrl(id), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al eliminar permanentemente la alergia:', error);
    throw error;
  }
};

export const bulkActionAllergies = async (bulkData) => {
  try {
    if (!bulkData || !bulkData.action || !bulkData.ids) {
      throw new Error('Datos de acción en lote son requeridos (action, ids)');
    }

    const response = await fetch(getAllergyBulkActionUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bulkData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error en acción masiva:', error);
    throw error;
  }
};

export const exportAllergies = async (exportParams = {}) => {
  try {
    const queryParams = new URLSearchParams(exportParams).toString();
    const url = queryParams ? `${getAllergyExportUrl()}?${queryParams}` : getAllergyExportUrl();

    const response = await fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return response.blob();
  } catch (error) {
    console.error('Error al exportar alergias:', error);
    throw error;
  }
};

export const getAllergyStatistics = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `${getAllergyStatisticsUrl()}?${queryParams}` : getAllergyStatisticsUrl();

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error al obtener estadísticas:', error);
    throw error;
  }
};